// src/pages/contentManagement/bibleVersions/bibleVersionList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Button, Pagination, Switch, Table, Tooltip } from "antd";
import { FaEye, FaEyeSlash, FaInfoCircle } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./bibleVersionList.css";

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  bibleVersions: [],
  originalVersions: {},
  currentPage: 1,
  pageSize: 10,
  sortConfig: { key: "versionName", direction: "asc" },
  changedVersions: {}, // Track visibility changes
  hasUnsavedChanges: false
};

// Reducer for state management
const bibleVersionReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setBibleVersions":
      const originals = {};
      action.payload.forEach((version) => {
        originals[version.versionId] = version.visible;
      });
      return {
        ...state,
        bibleVersions: action.payload,
        originalVersions: originals
      };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "toggleVisibility":
      const updatedVersions = state.bibleVersions.map((version) =>
        version.versionId === action.payload.versionId ? { ...version, visible: action.payload.visible } : version
      );

      // Build updated changedVersions
      const updatedChanges = { ...state.changedVersions };
      if (action.payload.visible === state.originalVersions[action.payload.versionId]) {
        delete updatedChanges[action.payload.versionId]; // Back to original, remove it
      } else {
        updatedChanges[action.payload.versionId] = action.payload.visible; // Still changed, keep it
      }

      return {
        ...state,
        bibleVersions: updatedVersions,
        changedVersions: updatedChanges,
        hasUnsavedChanges: Object.keys(updatedChanges).length > 0 // true only if something changed
      };
    case "clearChanges":
      return {
        ...state,
        changedVersions: {},
        hasUnsavedChanges: false
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function BibleVersionManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(bibleVersionReducer, initialState);
  const [updateLoading, setUpdateLoading] = useState(false);

  const { isLoading, searchTerm, filters, bibleVersions, currentPage, pageSize, sortConfig, changedVersions, hasUnsavedChanges } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      language: {
        label: "Language",
        options: [
          { value: "English", label: "English" },
          { value: "Kannada", label: "Kannada" },
          { value: "Spanish", label: "Spanish" },
          { value: "French", label: "French" },
          { value: "German", label: "German" },
          { value: "Portuguese", label: "Portuguese" }
        ]
      },
      visibility: {
        label: "Visibility",
        options: [
          { value: "visible", label: "Visible" },
          { value: "hidden", label: "Hidden" }
        ]
      }
    }),
    []
  );

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [item.title || "", item.versionId || "", item.language || "", item.uploadedAt || ""];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters and search
  const filteredData = useMemo(() => {
    let result = [...bibleVersions];

    // Apply language filter
    if (filters.language) {
      result = result.filter((version) => version.language === filters.language);
    }

    // Apply visibility filter
    if (filters.visibility) {
      const isVisible = filters.visibility === "visible";
      result = result.filter((version) => version.visible === isVisible);
    }

    // Apply global search
    result = globalSearch(result, searchTerm);

    // Apply sorting
    if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        let valA, valB;

        // Handle different field mappings
        if (key === "versionName") {
          valA = a.title?.toString().toLowerCase() || "";
          valB = b.title?.toString().toLowerCase() || "";
        } else {
          valA = a[key]?.toString().toLowerCase() || "";
          valB = b[key]?.toString().toLowerCase() || "";
        }

        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [bibleVersions, filters, searchTerm, globalSearch, sortConfig]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Fetch Bible Versions - GET all versions (admin endpoint)
  const fetchBibleVersions = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.BIBLE_VERSIONS, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 && response.data?.data) {
        // Transform API response to match component expectations
        const transformedData = response.data.data.map((version) => ({
          versionId: version.versionId,
          title: version.title,
          versionName: version.title, // For table display
          visible: version.visible,
          language: version.language || "English",
          uploadedAt: version.uploadedAt || version.createdAt || new Date().toISOString(),
          size: version.size || "N/A",
          thumbnailUrl: version.thumbnail || null,
          id: version.versionId // For compatibility with existing code
        }));

        dispatch({ type: "setBibleVersions", payload: transformedData });
        showNotification(`Loaded ${transformedData.length} Bible versions successfully`, "success");
      } else {
        throw new Error("Unexpected API response format.");
      }
    } catch (error) {
      console.error("Error fetching Bible versions:", error);

      if (error.response?.status === 401) {
        showNotification("Authentication failed. Please login again.", "error");
      } else if (error.response?.status === 403) {
        showNotification("Access denied. Admin privileges required.", "error");
      } else {
        showNotification("Failed to load Bible versions. Please try again.", "error");
      }

      // Set empty array on error
      dispatch({ type: "setBibleVersions", payload: [] });
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  // Handle Visibility Toggle
  const handleVisibilityToggle = useCallback((versionId, checked) => {
    dispatch({
      type: "toggleVisibility",
      payload: { versionId, visible: checked }
    });
  }, []);

  // Handle Update Changes - PATCH visibility for changed versions
  const handleUpdateChanges = useCallback(async () => {
    if (!hasUnsavedChanges) {
      showNotification("No changes to update.", "info");
      return;
    }

    setUpdateLoading(true);

    try {
      const token = await getAuthToken();

      // Update each changed version individually
      const updatePromises = Object.entries(changedVersions).map(async ([versionId, visible]) => {
        const response = await axios.put(
          API_ENDPOINTS.BIBLE_VERSION_UPDATE(versionId),
          { visible },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        return { versionId, success: response.status === 200, response };
      });

      const results = await Promise.allSettled(updatePromises);

      // Check results
      const successCount = results.filter((result) => result.status === "fulfilled" && result.value.success).length;

      const totalCount = results.length;

      if (successCount === totalCount) {
        showNotification(`Successfully updated ${successCount} Bible version(s)!`, "success");
        dispatch({ type: "clearChanges" });
        await fetchBibleVersions(); // Refresh data
      } else if (successCount > 0) {
        showNotification(`Updated ${successCount} of ${totalCount} versions. Some updates failed.`, "warning");
        dispatch({ type: "clearChanges" });
        await fetchBibleVersions();
      } else {
        throw new Error("All updates failed");
      }
    } catch (error) {
      console.error("Update failed:", error.response?.data || error.message);

      if (error.response?.status === 401) {
        showNotification("Authentication failed. Please login again.", "error");
      } else if (error.response?.status === 403) {
        showNotification("Access denied. Admin privileges required.", "error");
      } else {
        showNotification(error.response?.data?.message || "Update failed. Please try again.", "error");
      }
      setUpdateLoading(false);
    } finally {
      setUpdateLoading(false);
    }
  }, [hasUnsavedChanges, changedVersions, fetchBibleVersions, showNotification]);

  // Event handlers
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "VERSION ID",
        dataIndex: "versionId",
        sorter: (a, b) => (a.versionId || "").localeCompare(b.versionId || ""),
        render: (versionId) => versionId || "-",
        width: 120
      },
      {
        title: "VERSION NAME",
        dataIndex: "versionName",
        sorter: (a, b) => (a.versionName || "").localeCompare(b.versionName || ""),
        render: (versionName) => versionName || "-",
        width: 150,
        ellipsis: true
      },
      {
        title: "LANGUAGE",
        dataIndex: "language",
        sorter: (a, b) => (a.language || "").localeCompare(b.language || ""),
        render: (language) => language || "English",
        width: 100
      },
      {
        title: "UPLOADED DATE",
        dataIndex: "uploadedAt",
        sorter: (a, b) => {
          const dateA = new Date(a.uploadedAt || 0).getTime();
          const dateB = new Date(b.uploadedAt || 0).getTime();
          return dateA - dateB;
        },
        render: (uploadedAt) => {
          if (!uploadedAt) return "-";
          try {
            const date = new Date(uploadedAt);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric"
            });
          } catch (error) {
            return "-";
          }
        },
        width: 140,
        ellipsis: false,
        className: "nowrap-header"
      },
      {
        title: "SIZE",
        dataIndex: "size",
        sorter: (a, b) => {
          const sizeA = parseFloat(a.size?.replace(/[^\d.]/g, "") || 0);
          const sizeB = parseFloat(b.size?.replace(/[^\d.]/g, "") || 0);
          return sizeA - sizeB;
        },
        render: (size) => size || "-",
        width: 80
      },
      {
        title: "VISIBILITY",
        render: (_, record) => (
          <div className="bevm-list-btn-group" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={record.visible ? "Hide" : "Show"}>
              <Switch
                checked={record.visible}
                onChange={(checked) => handleVisibilityToggle(record.versionId, checked)}
                checkedChildren={<FaEye />}
                unCheckedChildren={<FaEyeSlash />}
                style={{ width: "4em" }}
              />
            </Tooltip>
          </div>
        ),
        width: 120
      }
    ],
    [currentPage, pageSize, handleVisibilityToggle]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((version, index) => ({
      "SL. NO.": index + 1,
      "Version ID": version.versionId,
      "Version Name": version.versionName,
      Language: version.language,
      "Uploaded Date": version.uploadedAt ? new Date(version.uploadedAt).toLocaleDateString("en-US") : "-",
      Size: version.size,
      Visibility: version.visible ? "Visible" : "Hidden"
    }));
  }, [filteredData]);

  // Effects
  useEffect(() => {
    fetchBibleVersions();
  }, [fetchBibleVersions]);

  return (
    <Layout
      navTitle="Content Management"
      pageTitle={
        <>
          Bible Versions
          <Tooltip
            title="Preferred version not found? Contact the support team for more info."
            placement="right"
            styles={{ body: { width: "40em" } }}
          >
            <FaInfoCircle
              style={{
                fontSize: "12px",
                color: "hsl(0, 0%, 50%)",
                margin: "0 0 8px 4px"
              }}
            />
          </Tooltip>
        </>
      }
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="bevm-page-wrapper">
        <TableLayout
          toolbar={
            <TopToolBar
              filtersConfig={filtersConfig}
              searchPlaceholder="Search Bible versions..."
              searchTerm={searchTerm}
              filters={filters}
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              onReset={handleReset}
              showExcelDownload={true}
              excelData={prepareExcelData()}
              excelFilename="bible-versions"
              excelSheetName="Bible Versions"
            />
          }
        >
          <Table
            className="tpl-table"
            rowKey={(record) => record.versionId}
            rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
            columns={columns}
            dataSource={currentPageData}
            loading={isLoading}
            pagination={false}
            expandable={{
              showExpandColumn: false
            }}
          />
          <div className="tpl-pagination-wrapper">
            <div className="bevm-pagination-update-btn">
              <Pagination
                className="tpl-pagination"
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                onChange={handlePaginationChange}
                showSizeChanger={false}
              />
              <Button
                onClick={handleUpdateChanges}
                loading={updateLoading}
                disabled={!hasUnsavedChanges || updateLoading}
                className="bevm-update-btn"
              >
                {updateLoading ? "Updating..." : `Update Changes ${hasUnsavedChanges ? `(${Object.keys(changedVersions).length})` : ""}`}
              </Button>
            </div>
          </div>

          {hasUnsavedChanges && (
            <div
              style={{
                margin: "10px",
                padding: "10px",
                backgroundColor: "hsl(41, 100%, 95%)",
                border: "1px solid hsl(37, 100%, 78%)",
                borderRadius: "4px",
                fontSize: "14px"
              }}
            >
              <strong>Unsaved Changes:</strong> You have {Object.keys(changedVersions).length} pending visibility change(s). Click "Update
              Changes" to save your modifications.
            </div>
          )}
        </TableLayout>
      </div>
    </Layout>
  );
}

export default BibleVersionManagement;
